import { Check, Minus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'
import { useAuth } from '@/store/auth'

type Role = 'owner' | 'admin' | 'manager' | 'viewer'

const ROLES: Role[] = ['owner', 'admin', 'manager', 'viewer']
const ALL: Role[] = ROLES
const WRITE: Role[] = ['owner', 'admin', 'manager']
const ADMIN: Role[] = ['owner', 'admin']

/**
 * Čo smie ktorá rola - musí sedieť s backendom:
 * `apps/core/permissions.py` (ReadOnlyForViewers = zápis všetci okrem
 * prezerajúceho, IsAdmin / AdminOnly = vlastník a administrátor) a
 * `accounts.serializers.UserSerializer.validate_role` (rolu vlastník
 * prideľuje len vlastník). Prezerajúci smie len kalendár
 * (`accounts.authentication.viewer_allowed`).
 */
const MATRIX: Array<[string, Role[]]> = [
  ['viewCalendar', ALL],
  ['editBookings', WRITE],
  ['payments', WRITE],
  ['bankImport', WRITE],
  ['rewards', WRITE],
  ['changelog', WRITE],
  ['revert', WRITE],
  ['statistics', ADMIN],
  ['statisticsExport', ADMIN],
  ['emails', ADMIN],
  ['settings', ADMIN],
  ['users', ADMIN],
  ['assignOwner', ['owner']],
  ['documents', ADMIN],
  ['ownAccount', ALL],
]

/** Roly a oprávnenia - prehľad pre vlastníka a administrátora, kým prideľujú
 *  roly v záložke Používatelia. */
export default function RolesTab() {
  const { t } = useTranslation()
  const mine = useAuth((s) => s.user)?.role as Role | undefined

  return (
    <div className="max-w-4xl space-y-6">
      <p className="text-sm text-slate-600">{t('roles.intro')}</p>

      <ul className="grid gap-3 sm:grid-cols-2">
        {ROLES.map((role) => (
          <li
            key={role}
            className={cn(
              'rounded-md border p-3',
              role === mine ? 'border-brand-300 bg-brand-50/40' : 'border-slate-200 bg-white',
            )}
          >
            <p className="text-sm font-semibold">
              {t(`settings.roles.${role}`)}
              {role === mine ? (
                <span className="ml-2 rounded bg-brand-100 px-1.5 py-0.5 text-[11px] font-medium text-brand-700">
                  {t('roles.yourRole')}
                </span>
              ) : null}
            </p>
            <p className="mt-1 text-sm text-slate-600">{t(`roles.describe.${role}`)}</p>
          </li>
        ))}
      </ul>

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="table-base">
          <thead>
            <tr>
              <th>{t('roles.permission')}</th>
              {ROLES.map((role) => (
                <th key={role} className={cn('text-center', role === mine && 'bg-brand-50/60')}>
                  {t(`settings.roles.${role}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MATRIX.map(([key, allowed]) => (
              <tr key={key}>
                <td>{t(`roles.can.${key}`)}</td>
                {ROLES.map((role) => (
                  <td key={role} className={cn('text-center', role === mine && 'bg-brand-50/40')}>
                    {allowed.includes(role) ? (
                      <Check size={16} className="mx-auto text-emerald-600" aria-label="✓" />
                    ) : (
                      <Minus size={14} className="mx-auto text-slate-300" aria-label="–" />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500">{t('roles.footnote')}</p>
    </div>
  )
}
